import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Find the blog post with the matching ID
  const blog = contentData.blogs?.find((blog) => blog.id === id);

  // If blog post is not found, redirect to blog listing page
  useEffect(() => {
    if (!blog) {
      navigate('/blog');
    }
  }, [blog, navigate]);

  if (!blog) {
    return (
      <>
        <SEO 
          title="Blog Post Not Found"
          description="The blog post you're looking for doesn't exist or has been moved."
          noindex={true}
        />
        <div className="py-12 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
          <Link to="/blog" className="text-blue-600 hover:text-blue-800">
            Return to Blog
          </Link>
        </div>
      </>
    );
  }

  // SEO data for the blog post
  const seoData = {
    title: blog.title,
    description: blog.summary || blog.content?.substring(0, 160) + '...',
    keywords: `${blog.title}, ${contentData.company.name} Blog, Cloud Technology, AWS, DevOps`,
    type: 'article',
    image: blog.image
  };

  return (
    <>
      <SEO {...seoData} />

      <article className="py-12 px-4 sm:px-8 md:px-16 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center text-blue-600 mb-8 hover:text-blue-800 transition-colors"
            aria-label="Back to blog listing"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Blog
          </Link>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {blog.title}
            </h1>
            <div className="flex items-center text-sm text-slate-500 mb-6">
              <time dateTime={blog.date}>{blog.date}</time>
              <span className="mx-2" aria-hidden="true">•</span>
              <span>{blog.readTime}</span>
            </div>
          </header>

          {blog.image && (
            <div className="mb-8 rounded-xl overflow-hidden shadow-md">
              <img 
                src={blog.image} 
                alt={blog.title} 
                loading="lazy"
                className="w-full h-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/placeholder.svg';
                }}
              />
            </div>
          )}

          <div 
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-blue-600"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
          
          <footer className="mt-12 pt-6 border-t border-gray-200">
            <Link 
              to="/blog" 
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              aria-label="Return to all blog articles"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back to All Articles
            </Link>
          </footer>
        </motion.div>
      </article>
    </>
  );
};

export default BlogDetail;